import { ScoreIssue, ScoreIssueKind } from "./scoreIssues";
import { scoreStepPath } from "./scoreSteps";

/**
 * The `?focus=` deep-link a tab's issue list navigates to (PREC-4055): the page
 * that opens selects the entity, scrolls it into view and flashes it once.
 *
 * Format: `<kind>:<id>` or `<kind>:<id>:<personaId>` for a matrix cell. The
 * segment key may itself contain `|` (investor rows), never `:`.
 */

export const FOCUS_PARAM = "focus";

export interface FocusTarget {
	kind: ScoreIssueKind;
	id: string;
	personaId?: number;
}

const KINDS: readonly ScoreIssueKind[] = ["general", "segment", "persona", "scoring", "cell"];

export const focusParamOf = (issue: ScoreIssue): string =>
	[issue.kind, issue.id, issue.personaId].filter((part) => part !== undefined).join(":");

export const parseFocusParam = (value: string | null | undefined): FocusTarget | null => {
	if (!value) {
		return null;
	}

	const [kind, id, personaId] = value.split(":");

	if (!KINDS.includes(kind as ScoreIssueKind) || !id) {
		return null;
	}

	const personaNumber = personaId === undefined ? undefined : Number(personaId);

	if (personaNumber !== undefined && Number.isNaN(personaNumber)) {
		return null;
	}

	return { kind: kind as ScoreIssueKind, id, personaId: personaNumber };
};

/** Where clicking an issue takes the user. */
export const issueHref = (issue: ScoreIssue): string =>
	`${scoreStepPath(issue.step)}?${FOCUS_PARAM}=${encodeURIComponent(focusParamOf(issue))}`;

/** Whether a focus target points at the given entity (and persona, when the target has one). */
export const focusMatches = (
	target: FocusTarget | null,
	kind: ScoreIssueKind,
	id: string,
	personaId?: number
): boolean =>
	!!target &&
	target.kind === kind &&
	target.id === id &&
	(target.personaId === undefined || target.personaId === personaId);
